import React from 'react';
import { Headline, Text } from '@oneaudi/unified-web-components';
import { CardWrapper, StyledButton, StyledImage, StyledSliderItem } from './CarGroupStyles';
import type { MoxxCarlineGroup } from '../../../@types/MoxxTypes';
import { useAppContext } from '../../context/Context';
import { ArrowIcon } from '../ArrowIcon';
import { getOptimizedImageSrc } from '../../utils/imgUtils';
import { useTrackingManager } from '../../context/useTrackingManager';
import {
  IMAGE_MIME_TYPE,
  CARGROUP_IMAGE_HEIGHT,
  CARGROUP_IMAGE_WIDTH,
  CARGROUP_SCALED_IMAGE_WIDTH,
} from '../../constants';

interface CarGroupCardProps {
  carlineGroup: MoxxCarlineGroup;
  isSelected: boolean;
  onClick: () => void;
  onFocus?: () => void;
  amountCarlines: string;
  dataTestId: string;
}

const CarGroupCard: React.ForwardRefRenderFunction<HTMLLIElement, CarGroupCardProps> = (
  { carlineGroup, isSelected, onClick, amountCarlines, dataTestId, onFocus },
  ref,
) => {
  const { selectedCarGroupId } = useAppContext();
  const cardImageSrc = getOptimizedImageSrc(carlineGroup.carlines[0].image, {
    extension: IMAGE_MIME_TYPE,
    width: '180',
  });
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { trackExpandCardClick } = useTrackingManager();

  return (
    <CardWrapper
      key={carlineGroup.id}
      ref={ref}
      tabIndex={0}
      aria-label={carlineGroup.name}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          onClick();
          trackExpandCardClick(carlineGroup.name, carlineGroup.id, 'image');
        }
      }}
      id="triggerButton"
      role="listitem"
    >
      <StyledSliderItem
        className={isSelected ? 'selected' : ''}
        data-testid={dataTestId}
        onClick={() => {
          onClick();
          trackExpandCardClick(carlineGroup.name, carlineGroup.id, 'image');
        }}
      >
        <Headline
          tag="span"
          variant={{
            order: '4',
            type: 'headline',
            weight: 'normal',
          }}
        >
          {carlineGroup.name}
        </Headline>
        <StyledImage
          src={getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH })}
          alt={carlineGroup.name}
          width={CARGROUP_IMAGE_WIDTH}
          height={CARGROUP_IMAGE_HEIGHT}
          srcSet={`${getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH })} ${CARGROUP_IMAGE_WIDTH}w, ${getOptimizedImageSrc(cardImageSrc, { width: CARGROUP_SCALED_IMAGE_WIDTH * 2 })} ${CARGROUP_IMAGE_WIDTH * 2}w`}
          sizes={`${CARGROUP_IMAGE_WIDTH}px`}
          loading="lazy"
        />
        <StyledButton
          data-testid={carlineGroup.id}
          id="triggerButton"
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              onClick();
              trackExpandCardClick(carlineGroup.name, carlineGroup.id, 'image');
            }
          }}
        >
          <Text variant={{ type: 'copy', order: '1', style: 'normal' }}>{amountCarlines}</Text>
          <ArrowIcon rotated={carlineGroup.id === selectedCarGroupId} />
        </StyledButton>
      </StyledSliderItem>
    </CardWrapper>
  );
};
export default React.forwardRef(CarGroupCard);
